/**
 * Ads config, guardrails, and native SDK bootstrap.
 *
 * The rules this module enforces come straight from
 * docs/product-brief.md § "Ad placement rules (design-level, non-negotiable)":
 *
 *   • No banner ads on write flows (enforced by not placing InlineAd there)
 *   • No interstitials on in-app navigation. Interstitials only fire at
 *     completion beats — after the user finishes reading a recap, or right
 *     after they successfully create a trip. Both are natural "task done"
 *     moments where a takeover doesn't interrupt an ongoing thought.
 *   • Contained slots only (InlineAd owns the container; the native banner is
 *     pinned to it and clipped under the sticky header / bottom bar)
 *   • One ad per screen height maximum (enforced by placement in routes)
 *   • Native feel — the banner sits in the list as a row and scrolls with it
 *     (app-local InlineBanner plugin), no separate "Sponsored" strip
 *   • Reserved ad space — InlineAd reserves a loading row, resizes to the
 *     served banner's height, and collapses only on no-fill
 *   • No frequency cap — the app is ad-funded; volume is governed by placement
 *     (one slot per screen) and AdMob's own refresh, not a client-side counter
 *   • Kill switch — flip flags below and cut a new release, or wire Remote Config later
 *
 * We use the AdMob SDK on native Android and render no ad slot at all on the
 * web build (iOS PWA + browser). AdSense wiring on the web is a follow-up:
 * we don't have an AdSense publisher id yet, only AdMob unit ids.
 */

import { Capacitor } from "@capacitor/core";

import type { AdSlotName } from "@/components/inline-ad";

// ─── Unit IDs ────────────────────────────────────────────────────────────────
// Live IDs from the AdMob account. In dev builds we swap in Google's official
// test unit IDs so we never risk invalid-traffic strikes tapping our own ads.
// See https://developers.google.com/admob/android/test-ads

const LIVE_UNITS = {
  appId: "ca-app-pub-7082278595197706~9426969235",
  appOpen: "ca-app-pub-7082278595197706/6942392224",
  interstitial: "ca-app-pub-7082278595197706/2034139493",
  banners: {
    dashboard: "ca-app-pub-7082278595197706/4005098078", // banner_1
    // banner_2 was `timeline` before the trip layout moved the ad from
    // the Timeline route into the shared trip header; same AdMob unit,
    // renamed to reflect the new placement.
    trip: "ca-app-pub-7082278595197706/9169794847", // banner_2
    // banner_3 (expenses) and banner_4 held in reserve — the expenses tab no
    // longer carries a slot; keep the units so we can swap them back in without
    // regenerating IDs.
    reservedExpenses: "ca-app-pub-7082278595197706/5394201362",
    reserved: "ca-app-pub-7082278595197706/3599927483",
  },
} as const;

const TEST_UNITS_ANDROID = {
  appId: "ca-app-pub-3940256099942544~3347511713",
  appOpen: "ca-app-pub-3940256099942544/9257395921",
  interstitial: "ca-app-pub-3940256099942544/1033173712",
  banners: {
    dashboard: "ca-app-pub-3940256099942544/6300978111",
    trip: "ca-app-pub-3940256099942544/6300978111",
    reservedExpenses: "ca-app-pub-3940256099942544/6300978111",
    reserved: "ca-app-pub-3940256099942544/6300978111",
  },
} as const;

const isDev = import.meta.env.DEV;
export const AD_UNITS = isDev ? TEST_UNITS_ANDROID : LIVE_UNITS;

// ─── Feature flags ───────────────────────────────────────────────────────────
// Static defaults for now — swap the reader for Firebase Remote Config later
// without touching callers. Everything else in this file reads via getAdsConfig().

interface AdsConfig {
  /** Global kill switch. Flip to false to zero every slot in the next release. */
  enabled: boolean;
  /** Per-slot toggles so we can dark-launch one at a time. */
  slots: Record<AdSlotName, boolean>;
  /** Interstitials require an explicit opt-in — brief bans navigation interstitials. */
  interstitial: {
    /** Recap-completion interstitial. Off at launch; toggle on after banners are stable. */
    recap: boolean;
    /**
     * Trip-created interstitial — fires right after `createTripWithUser`
     * resolves. This is the highest-intent moment we have (user just chose to
     * commit to a trip) so it monetizes best. The takeover slots into the
     * "trip is being set up" beat so it doesn't feel like an interruption.
     */
    tripCreated: boolean;
  };
}

const DEFAULT_CONFIG: AdsConfig = {
  enabled: true,
  slots: {
    dashboard: true,
    trip: true,
  },
  interstitial: {
    recap: false,
    tripCreated: true,
  },
};

export function getAdsConfig(): AdsConfig {
  // TODO(remote-config): read overrides from Firebase Remote Config; the config
  // shape above is intentionally flat so the RC keys can mirror it 1:1 later.
  return DEFAULT_CONFIG;
}

// ─── Enablement ──────────────────────────────────────────────────────────────

export type AdPlatform = "android-native" | "ios-native" | "web";

export function adPlatform(): AdPlatform {
  if (!Capacitor.isNativePlatform()) return "web";
  return Capacitor.getPlatform() === "ios" ? "ios-native" : "android-native";
}

/**
 * Should this slot try to render an ad right now? Returns false when:
 *   • ads are globally off, or the specific slot is off
 *   • the platform can't render (iOS-native — no AdMob SDK wired yet)
 *
 * When this returns false, AdSlot still renders its reserved container so the
 * layout never shifts (invariant #7 in the brief).
 */
export function slotShouldRender(name: AdSlotName): boolean {
  return adSuppressionReason(name) === null;
}

export type AdSuppressionReason = "disabled" | "slot_off";

/** Why `name` won't render right now, or null when it should. */
export function adSuppressionReason(name: AdSlotName): AdSuppressionReason | null {
  const cfg = getAdsConfig();
  if (!cfg.enabled) return "disabled";
  if (!cfg.slots[name]) return "slot_off";
  return null;
}

/**
 * Report an ad outcome to Firebase Analytics. Release builds don't forward
 * `console` to logcat (Capacitor's default loggingBehavior), so without this
 * a production "no ads anywhere" is undiagnosable. Look for these events in
 * Firebase → Analytics → Events (or DebugView):
 *
 *   ad_slot_suppressed {slot, reason}          — never requested (flags)
 *   ad_request_failed  {slot, code, message}   — AdMob error / no fill / plugin missing
 *   ad_loaded          {slot, height}
 */
export function reportAdEvent(
  name: "ad_slot_suppressed" | "ad_request_failed" | "ad_loaded",
  params: Record<string, string | number>,
): void {
  if (isDev) console.info(`[ads] ${name}`, params);
  // Lazy import: keeps the analytics plugin out of this module's import graph
  // (ads.ts is imported by components that tests render without it).
  void import("./analytics")
    .then(({ logAnalyticsEvent }) => logAnalyticsEvent(name, params))
    .catch(() => undefined);
}

// ─── Native SDK bootstrap ────────────────────────────────────────────────────
// Dynamic import so the web bundle doesn't pull in ~40 KB of AdMob JS glue.
//
// Promise-cached: capacitor-glue fires initAdsNative() at boot, and every
// callsite that touches the SDK (InlineAd, maybeShow*Interstitial)
// awaits it — same promise, so callsites that race the boot call wait for the
// same resolution instead of calling AdMob.initialize a second time or firing
// showBanner against an uninitialized SDK.

let initPromise: Promise<void> | null = null;

export function initAdsNative(): Promise<void> {
  if (initPromise) return initPromise;
  if (adPlatform() !== "android-native") return Promise.resolve();
  if (!getAdsConfig().enabled) return Promise.resolve();
  initPromise = (async () => {
    try {
      const { AdMob } = await import("@capacitor-community/admob");
      await AdMob.initialize({
        initializeForTesting: isDev,
        // Conservative defaults so we never accidentally serve mature ads.
        tagForChildDirectedTreatment: false,
        tagForUnderAgeOfConsent: false,
      });
      if (isDev) console.info("[ads] AdMob initialized");
    } catch (err) {
      // Non-fatal: the app runs without ads if the SDK fails to init.
      console.warn("[ads] AdMob init failed", err);
    }
  })();
  return initPromise;
}

// ─── Interstitial ────────────────────────────────────────────────────────────

/**
 * Show one interstitial after the recap has been read, if enabled.
 * The brief bans navigation interstitials — this fires only when the user
 * finishes reading their trip recap, which is a natural completion moment.
 * Always no-ops on web or when the flag is off.
 */
export async function maybeShowRecapInterstitial(): Promise<void> {
  const cfg = getAdsConfig();
  if (!cfg.interstitial.recap) return;
  if (!cfg.enabled) return;
  if (adPlatform() !== "android-native") return;
  try {
    const { AdMob } = await import("@capacitor-community/admob");
    await AdMob.prepareInterstitial({
      adId: AD_UNITS.interstitial,
      isTesting: isDev,
    });
    await AdMob.showInterstitial();
  } catch (err) {
    console.warn("[ads] interstitial failed", err);
  }
}

// ─── Trip-created interstitial ───────────────────────────────────────────────

/**
 * Fire one interstitial after a successful trip creation, if enabled.
 * The brief bans navigation interstitials — this fires on the transition INTO
 * a freshly-created trip, which is a natural completion moment (the user just
 * committed to setting up a trip and expects a brief pause while the app loads
 * the new detail view).
 *
 * Always no-ops on web or when the flag is off.
 */
export async function maybeShowTripCreatedInterstitial(): Promise<void> {
  const cfg = getAdsConfig();
  if (!cfg.enabled) return;
  if (!cfg.interstitial.tripCreated) return;
  if (adPlatform() !== "android-native") return;
  try {
    const { AdMob } = await import("@capacitor-community/admob");
    await AdMob.prepareInterstitial({
      adId: AD_UNITS.interstitial,
      isTesting: isDev,
    });
    await AdMob.showInterstitial();
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn("[ads] trip-created interstitial failed", err);
    }
  }
}
